import { useState, useEffect } from 'react';
import { useI18n } from '../../contexts/I18nContext';

/**
 * PWA Install Prompt — shows a floating banner to install the app.
 * Only appears when the browser fires the `beforeinstallprompt` event.
 */
const PWAInstallPrompt = () => {
  const { t } = useI18n();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already in standalone mode
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    // Check iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(ios);

    // Check if dismissed this session
    if (sessionStorage.getItem('prax_pwa_dismissed')) {
      setDismissed(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => { setIsInstalled(true); setDeferredPrompt(null); });
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setDeferredPrompt(null);
    sessionStorage.setItem('prax_pwa_dismissed', '1');
  };

  if (isInstalled || dismissed) return null;

  // Show iOS hint if applicable
  if (isIOS && !deferredPrompt) {
    const iosDismissed = sessionStorage.getItem('prax_ios_dismissed');
    if (iosDismissed) return null;

    return (
      <div style={{
        position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
        zIndex: 9999, maxWidth: '420px', width: 'calc(100% - 32px)',
        background: '#fff', borderRadius: '14px', padding: '1rem 1.25rem',
        boxShadow: '0 8px 30px rgba(0,0,0,0.2)', border: '1px solid #E2E8F0',
        animation: 'slideUp 0.4s ease-out', fontFamily: "'DM Sans', sans-serif",
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div style={{ fontSize: '1.8rem', lineHeight: 1, flexShrink: 0 }}>📲</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1B3A5C', marginBottom: '0.3rem' }}>
              {t('pwa_installTitle')}
            </div>
            <div style={{ fontSize: '0.78rem', color: '#718096', lineHeight: 1.5 }}>
              {t('pwa_iosDesc')}
            </div>
          </div>
          <button onClick={() => { sessionStorage.setItem('prax_ios_dismissed', '1'); setDismissed(true); }} style={{
            background: 'none', border: 'none', color: '#CBD5E0', cursor: 'pointer', fontSize: '1.2rem', padding: 0,
          }}>×</button>
        </div>
        <style>{`@keyframes slideUp { from { transform: translateX(-50%) translateY(30px); opacity: 0; } to { transform: translateX(-50%) translateY(0); opacity: 1; } }`}</style>
      </div>
    );
  }

  // Standard install prompt (Chrome, Edge, etc.)
  if (!deferredPrompt) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
      zIndex: 9999, maxWidth: '420px', width: 'calc(100% - 32px)',
      background: 'linear-gradient(135deg, #1B3A5C, #2D5A8E)',
      borderRadius: '14px', padding: '1rem 1.25rem',
      boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
      display: 'flex', alignItems: 'center', gap: '0.75rem',
      animation: 'slideUp 0.4s ease-out', fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{
        width: '42px', height: '42px', borderRadius: '10px', flexShrink: 0,
        background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <img src="/images/icon-96.png" alt="" style={{ width: 32, height: 32, borderRadius: 6 }} onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<i class="bi bi-phone" style="color:#1E6FBA;font-size:1.3rem"></i>'; }} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.15rem' }}>
          {t('pwa_installTitle')}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
          {t('pwa_installDesc')}
        </div>
      </div>
      <button onClick={handleInstall} style={{
        background: '#1E6FBA', color: '#fff', border: 'none',
        borderRadius: '8px', padding: '0.4rem 0.8rem',
        fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
      }}>{t('pwa_install')}</button>
      <button onClick={handleDismiss} style={{
        background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
        cursor: 'pointer', fontSize: '1.1rem', padding: '0.2rem',
      }}>&times;</button>
      <style>{`@keyframes slideUp { from { transform: translateX(-50%) translateY(30px); opacity: 0; } to { transform: translateX(-50%) translateY(0); opacity: 1; } }`}</style>
    </div>
  );
};

export default PWAInstallPrompt;
